import React, { useState, useMemo, useEffect } from "react";
import { Banner, Image, Button, Input, TabsList, Text, Progress } from "tmaui";
import { IconArchive, IconSignature, IconDownload } from "@tabler/icons-react";
import { useApps } from "../../hooks/useApps";
import styles from "./AppList.module.css";
import type { TabType } from "../../types/api";

export const AppList = () => {
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("current");
  const { apps, loading, error, fetchApps, handleArchiveToggle } =
    useApps(activeTab);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const filteredApps = useMemo(() => {
    const searchLower = search.toLowerCase();
    return (apps || []).filter(
      (app) =>
        app.bundle_name.toLowerCase().includes(searchLower) ||
        app.description.toLowerCase().includes(searchLower) ||
        app.identifier.toLowerCase().includes(searchLower)
    );
  }, [search, apps]);

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.loadingContainer}>
          <Progress />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorContainer}>
          <Text weight="2">{error}</Text>
        </div>
      );
    }

    if (filteredApps.length === 0) {
      return (
        <div className={styles.emptyState}>
          <Text weight="2">
            {search ? "No results found" : `No ${activeTab} apps available`}
          </Text>
        </div>
      );
    }

    return filteredApps.map((app) => (
      <Banner
        key={app.identifier}
        className={styles.banner}
        before={<Image size={48} src={app.icon} />}
        callout={app.identifier}
        description={app.description}
        header={app.bundle_name}
        subheader={`v${app.version}`}
        type="inline"
      >
        <>
          <Button size="s" before={<IconDownload />}>
            Install
          </Button>
          <Button
            mode="plain"
            size="s"
            before={<IconArchive />}
            onClick={() => handleArchiveToggle(app)}
          >
            {app.archived ? "Unarchive" : "Archive"}
          </Button>
          <Button mode="plain" size="s" before={<IconSignature />}>
            Resign
          </Button>
        </>
      </Banner>
    ));
  };

  return (
    <div className={styles.container}>
      <TabsList>
        <TabsList.Item
          onClick={() => setActiveTab("current")}
          selected={activeTab === "current"}
        >
          Current
        </TabsList.Item>
        <TabsList.Item
          onClick={() => setActiveTab("archived")}
          selected={activeTab === "archived"}
        >
          Archived
        </TabsList.Item>
        <TabsList.Item
          onClick={() => setActiveTab("all")}
          selected={activeTab === "all"}
        >
          All
        </TabsList.Item>
      </TabsList>

      <div className={styles.searchContainer}>
        <Input
          className={styles.search}
          placeholder="Search apps..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {renderContent()}
    </div>
  );
};
